import React, { useEffect, useMemo, useState } from 'react';
import {
  AlertTriangle,
  ChevronDown,
  ChevronRight,
  CheckCircle,
  ClipboardList,
  Copy,
  FileText,
  History,
  KeyRound,
  Lock,
  Package,
  Pencil,
  Plus,
  Printer,
  RotateCw,
  Save,
  Search,
  ShoppingCart,
  Trash2,
  X
} from 'lucide-react';
import { EventOption, EventStatus, InventoryAccessCode, InventoryAccessCodeMap, InventoryEventOption, InventoryItem, InventoryLog, InventoryRequestStatus, ReplenishmentRequest } from '../types';
import {
  copyInventoryItemsFromEvent,
  createReplenishmentRequest,
  deleteInventoryItem,
  getInventoryAccessCodes,
  saveInventoryAccessCode,
  saveInventoryItem,
  subscribeToInventoryItems,
  subscribeToInventoryLogs,
  subscribeToReplenishmentRequests,
  updateReplenishmentRequestItems,
  updateReplenishmentRequestStatus
} from '../services/firebaseService';
import { sanitizeText } from '../utils/sanitize';
import { AutoReplenishmentProposal, buildAutoReplenishmentProposals } from '../utils/autoReplenishment';
import { findPreviousEventWithItems } from '../utils/inventoryCarryForward';
import { chooseDefaultInventoryEventId } from '../utils/inventoryEventSelection';
import { generateInventoryAccessCode } from '../utils/inventoryAccessCode';
import { buildInventoryInviteMessage } from '../utils/inventoryAccessInvite';
import { ToastType } from './Toast';

const DEFAULT_DEPARTMENTS = ['中醫', '西醫（外科）', '物理治療', '掛號', '藥局'];
const DEFAULT_UNITS = ['包', '盒', '支', '台', '個', '瓶', '袋', '捲', '片', '組'];
const UNARRANGED_LOCATION_PATTERN = /(尚無安排|未安排|待安排|無安排)/;

/** 與 functions/src/inventoryAccessCodes.ts 的 ACCESS_SESSION_DURATION_MS 對齊，只用於邀請訊息文案。 */
const ACCESS_SESSION_HOURS = 14;

/**
 * 現場常是手機開後台，clipboard API 在非 HTTPS 或舊瀏覽器會不存在，
 * 所以保留 execCommand 的退路，複製失敗要讓呼叫端知道。
 */
const copyTextToClipboard = async (text: string): Promise<boolean> => {
  try {
    if (navigator.clipboard?.writeText) {
      await navigator.clipboard.writeText(text);
      return true;
    }
  } catch {
    // 掉到下面的 fallback。
  }

  try {
    const textarea = document.createElement('textarea');
    textarea.value = text;
    textarea.setAttribute('readonly', '');
    textarea.style.position = 'fixed';
    textarea.style.opacity = '0';
    document.body.appendChild(textarea);
    textarea.select();
    const copied = document.execCommand('copy');
    document.body.removeChild(textarea);
    return copied;
  } catch {
    return false;
  }
};

type InventoryTab = 'stock' | 'auto-replenishment' | 'requests' | 'logs';

interface InventoryDraft {
  id?: string;
  department: string;
  name: string;
  quantity: string;
  unit: string;
  expiryDate: string;
  note: string;
}

interface RequestDraft {
  item: InventoryItem;
  requestedQuantity: string;
  note: string;
}

interface InventoryManagerProps {
  events: EventOption[];
  systemYear: number;
  currentUserLabel: string;
  showToast: (type: ToastType, title: string, message?: string) => void;
  /** 用通行碼進來的現場人員只能看自己那一梯，梯次下拉會被鎖住。 */
  lockedEventId?: string | null;
  /** 只有管理員能產生或停用梯次通行碼。 */
  canManageAccessCodes?: boolean;
}

const makeDraftFromItem = (item: InventoryItem): InventoryDraft => ({
  id: item.id,
  department: item.department,
  name: item.name,
  quantity: String(item.quantity),
  unit: item.unit,
  expiryDate: item.expiryDate || '',
  note: item.note || '',
});

const makeEmptyDraft = (department: string): InventoryDraft => ({
  department,
  name: '',
  quantity: '0',
  unit: '個',
  expiryDate: '',
  note: '',
});

const formatDateTime = (value?: string) => {
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleString('zh-TW', {
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  });
};

const getExpiryBadge = (expiryDate?: string) => {
  if (!expiryDate) return null;
  const expiry = new Date(`${expiryDate}T00:00:00`);
  if (Number.isNaN(expiry.getTime())) return null;

  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const diffDays = Math.ceil((expiry.getTime() - today.getTime()) / 86400000);

  if (diffDays < 0) {
    return { label: '已過期', className: 'bg-red-100 text-red-700 border-red-200' };
  }

  if (diffDays <= 30) {
    return { label: `${diffDays} 天內到期`, className: 'bg-red-100 text-red-700 border-red-200' };
  }

  if (diffDays <= 90) {
    return { label: `${diffDays} 天內到期`, className: 'bg-amber-100 text-amber-700 border-amber-200' };
  }

  return null;
};

const getExpiryStatus = (expiryDate?: string): 'expired' | 'soon' | 'clear' => {
  const badge = getExpiryBadge(expiryDate);
  if (!badge) return 'clear';
  return badge.label === '已過期' ? 'expired' : 'soon';
};

const formatExpiryDate = (expiryDate?: string) => expiryDate || '未填';

const getStatusLabel = (status: InventoryRequestStatus) => {
  if (status === 'handled') return '已處理';
  if (status === 'cancelled') return '取消';
  return '已送出';
};

const getStatusClass = (status: InventoryRequestStatus) => {
  if (status === 'handled') return 'bg-green-100 text-green-700 border-green-200';
  if (status === 'cancelled') return 'bg-gray-100 text-gray-600 border-gray-200';
  return 'bg-blue-100 text-blue-700 border-blue-200';
};

const getActionLabel = (action: InventoryLog['action']) => {
  switch (action) {
    case 'create':
      return '新增品項';
    case 'update':
      return '更新庫存';
    case 'delete':
      return '刪除品項';
    case 'replenishment_request':
      return '申請補貨';
    case 'request_status':
      return '更新補貨單';
    case 'request_update':
      return '修改補貨單內容';
    default:
      return action;
  }
};

const escapeHtml = (value: unknown) => String(value ?? '')
  .replace(/&/g, '&amp;')
  .replace(/</g, '&lt;')
  .replace(/>/g, '&gt;')
  .replace(/"/g, '&quot;')
  .replace(/'/g, '&#39;');

const formatPrintDateTime = () => new Date().toLocaleString('zh-TW', {
  year: 'numeric',
  month: '2-digit',
  day: '2-digit',
  hour: '2-digit',
  minute: '2-digit',
  hour12: false,
});

const parseIsoDate = (value?: string) => {
  if (!value) return null;
  const date = new Date(value.includes('T') ? value : `${value}T12:00:00`);
  return Number.isNaN(date.getTime()) ? null : date;
};

const parseEventBoundaryDate = (event: EventOption, boundary: 'start' | 'end') => {
  const isoDate = boundary === 'end'
    ? parseIsoDate(event.endDate) || parseIsoDate(event.startDate)
    : parseIsoDate(event.startDate) || parseIsoDate(event.endDate);
  if (isoDate) return isoDate;

  const match = (event.dateRange || '').match(
    /(\d{1,2})\s*(?:月|\/)\s*(\d{1,2})(?:\s*(?:-|－|–|—|~|～|至|到)\s*(?:(\d{1,2})\s*(?:月|\/)\s*)?(\d{1,2}))?/
  );
  if (!match) return null;

  const startMonth = Number(match[1]);
  const startDay = Number(match[2]);
  const endMonth = match[3] ? Number(match[3]) : startMonth;
  const endDay = match[4] ? Number(match[4]) : startDay;
  const month = boundary === 'end' ? endMonth : startMonth;
  const day = boundary === 'end' ? endDay : startDay;

  if (!Number.isFinite(month) || !Number.isFinite(day)) return null;

  const date = new Date(event.year || new Date().getFullYear(), month - 1, day, 12);
  return Number.isNaN(date.getTime()) ? null : date;
};

const getInventoryEventLabel = (event: EventOption) => `${event.dateRange || '未填日期'} - ${event.location || '未填地點'}`;

const isInventorySelectableEvent = (event: EventOption) => {
  const status = String(event.status || '').toLowerCase();
  const location = (event.location || '').trim();
  const dateRange = (event.dateRange || '').trim();

  if (!location || !dateRange) return false;
  if (event.status === EventStatus.NotArranged || status.includes('not_arranged') || status.includes('not-arranged')) {
    return false;
  }

  return !UNARRANGED_LOCATION_PATTERN.test(location);
};

const toInventoryEventOption = (event: EventOption): InventoryEventOption => ({
  id: event.id,
  label: getInventoryEventLabel(event),
  startTime: parseEventBoundaryDate(event, 'start')?.getTime() ?? null,
  endTime: parseEventBoundaryDate(event, 'end')?.getTime() ?? null,
});

const matchesSelectedInventoryEvent = (
  record: { eventId?: string; eventLabel?: string },
  selectedEventId: string,
  selectedEventLabel: string
) => {
  if (!selectedEventId) return !record.eventId && !record.eventLabel;
  return record.eventId === selectedEventId || (!record.eventId && record.eventLabel === selectedEventLabel);
};

const InventoryManager: React.FC<InventoryManagerProps> = ({
  events,
  systemYear,
  currentUserLabel,
  showToast,
  lockedEventId = null,
  canManageAccessCodes = false,
}) => {
  const [activeTab, setActiveTab] = useState<InventoryTab>('stock');
  const [items, setItems] = useState<InventoryItem[]>([]);
  const [requests, setRequests] = useState<ReplenishmentRequest[]>([]);
  const [logs, setLogs] = useState<InventoryLog[]>([]);
  const [selectedDepartment, setSelectedDepartment] = useState(DEFAULT_DEPARTMENTS[0]);
  const [selectedEventId, setSelectedEventId] = useState<string | null>(null);
  const [hasUserSelectedEvent, setHasUserSelectedEvent] = useState(false);
  const [accessCodes, setAccessCodes] = useState<InventoryAccessCodeMap>({});
  const [isSavingAccessCode, setIsSavingAccessCode] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [drafts, setDrafts] = useState<Record<string, InventoryDraft>>({});
  const [newItemDraft, setNewItemDraft] = useState<InventoryDraft>(makeEmptyDraft(DEFAULT_DEPARTMENTS[0]));
  const [requestDraft, setRequestDraft] = useState<RequestDraft | null>(null);
  const [selectedAutoProposalIds, setSelectedAutoProposalIds] = useState<Record<string, boolean>>({});
  const [requestQuantityDrafts, setRequestQuantityDrafts] = useState<Record<string, string>>({});
  const [openDepartments, setOpenDepartments] = useState<Record<string, boolean>>({});
  const [expandedItemIds, setExpandedItemIds] = useState<Record<string, boolean>>({});
  const [isNewItemOpen, setIsNewItemOpen] = useState(false);
  const [isSavingId, setIsSavingId] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isCopyingPrevious, setIsCopyingPrevious] = useState(false);

  useEffect(() => {
    setIsLoading(true);
    const unsubscribeItems = subscribeToInventoryItems(
      nextItems => {
        setItems(nextItems);
        setDrafts(prev => {
          const nextDrafts: Record<string, InventoryDraft> = {};
          nextItems.forEach(item => {
            nextDrafts[item.id] = prev[item.id] || makeDraftFromItem(item);
          });
          return nextDrafts;
        });
        setIsLoading(false);
      },
      error => {
        console.error(error);
        setIsLoading(false);
        showToast('error', '讀取失敗', '無法載入物資庫存');
      }
    );

    const unsubscribeRequests = subscribeToReplenishmentRequests(
      setRequests,
      error => {
        console.error(error);
        showToast('error', '讀取失敗', '無法載入補貨單');
      }
    );

    const unsubscribeLogs = subscribeToInventoryLogs(
      setLogs,
      error => {
        console.error(error);
        showToast('error', '讀取失敗', '無法載入修改紀錄');
      }
    );

    return () => {
      unsubscribeItems();
      unsubscribeRequests();
      unsubscribeLogs();
    };
  }, [showToast]);

  const departments = useMemo(() => {
    const departmentSet = new Set(DEFAULT_DEPARTMENTS);
    items.forEach(item => departmentSet.add(item.department));
    requests.forEach(request => departmentSet.add(request.department));
    return Array.from(departmentSet).sort((a, b) => {
      const aIndex = DEFAULT_DEPARTMENTS.indexOf(a);
      const bIndex = DEFAULT_DEPARTMENTS.indexOf(b);
      const normalizedAIndex = aIndex === -1 ? DEFAULT_DEPARTMENTS.length : aIndex;
      const normalizedBIndex = bIndex === -1 ? DEFAULT_DEPARTMENTS.length : bIndex;
      return normalizedAIndex - normalizedBIndex || a.localeCompare(b, 'zh-Hant');
    });
  }, [items, requests]);

  const activeEventOptions = useMemo(() => events
    .filter(isInventorySelectableEvent)
    .map(toInventoryEventOption)
    .sort((a, b) =>
      (a.startTime ?? Number.MAX_SAFE_INTEGER) - (b.startTime ?? Number.MAX_SAFE_INTEGER) ||
      a.label.localeCompare(b.label, 'zh-Hant')
    ), [events]);

  const defaultInventoryEventId = useMemo(
    () => lockedEventId || chooseDefaultInventoryEventId(activeEventOptions),
    [activeEventOptions, lockedEventId]
  );

  useEffect(() => {
    // 通行碼使用者的梯次由權杖決定，不受下拉或自動選梯的影響。
    if (lockedEventId) {
      if (selectedEventId !== lockedEventId) {
        setSelectedEventId(lockedEventId);
      }
      return;
    }

    const optionIds = new Set(activeEventOptions.map(option => option.id));
    if (!hasUserSelectedEvent && selectedEventId !== defaultInventoryEventId) {
      setSelectedEventId(defaultInventoryEventId);
      return;
    }

    if (selectedEventId && !optionIds.has(selectedEventId)) {
      setSelectedEventId(defaultInventoryEventId);
      setHasUserSelectedEvent(false);
    }
  }, [activeEventOptions, defaultInventoryEventId, hasUserSelectedEvent, lockedEventId, selectedEventId]);

  const selectedEventValue = lockedEventId || (selectedEventId ?? defaultInventoryEventId);
  const selectedInventoryEvent = activeEventOptions.find(event => event.id === selectedEventValue);
  const selectedEventLabel = selectedInventoryEvent?.label || '';
  const accessCodeEntry = accessCodes[selectedEventValue];

  useEffect(() => {
    if (!canManageAccessCodes) return;
    let cancelled = false;

    getInventoryAccessCodes().then(codes => {
      if (!cancelled) setAccessCodes(codes);
    });

    return () => { cancelled = true; };
  }, [canManageAccessCodes]);

  const persistAccessCode = async (entry: InventoryAccessCode, successMessage: string) => {
    if (!selectedEventValue) return;
    setIsSavingAccessCode(true);

    try {
      await saveInventoryAccessCode(selectedEventValue, entry, currentUserLabel);
      setAccessCodes(previous => ({
        ...previous,
        [selectedEventValue]: { ...entry, updatedBy: currentUserLabel },
      }));
      showToast('success', successMessage);
    } catch (error) {
      console.error('Failed to save inventory access code:', error);
      showToast('error', '通行碼儲存失敗', '請確認網路連線後再試一次。');
    } finally {
      setIsSavingAccessCode(false);
    }
  };

  const handleGenerateAccessCode = () => persistAccessCode(
    {
      code: generateInventoryAccessCode(),
      eventLabel: selectedEventLabel,
      active: true,
      expiresAt: null,
    },
    accessCodeEntry?.code ? '已重新產生通行碼' : '已產生通行碼'
  );

  const handleDisableAccessCode = () => {
    if (!accessCodeEntry) return;
    return persistAccessCode(
      { ...accessCodeEntry, active: false },
      '已停用通行碼'
    );
  };

  const inviteMessage = accessCodeEntry?.code
    ? buildInventoryInviteMessage({
        code: accessCodeEntry.code,
        eventLabel: selectedEventLabel,
        origin: window.location.origin,
        validHours: ACCESS_SESSION_HOURS,
      })
    : '';

  const handleCopyInvite = async () => {
    if (!inviteMessage) return;

    const copied = await copyTextToClipboard(inviteMessage);
    if (copied) {
      showToast('success', '已複製邀請訊息', '直接貼到 LINE 或簡訊傳給同工即可。');
    } else {
      showToast('error', '複製失敗', '請長按下方訊息手動複製。');
    }
  };

  const eventItems = useMemo(() =>
    items.filter(item => matchesSelectedInventoryEvent(item, selectedEventValue, selectedEventLabel)),
  [items, selectedEventLabel, selectedEventValue]);

  const previousEventWithItems = useMemo(
    () => findPreviousEventWithItems(activeEventOptions, items, selectedEventValue),
    [activeEventOptions, items, selectedEventValue]
  );

  const selectedStockItems = useMemo(() => {
    if (selectedDepartment === '全部') return eventItems;
    return eventItems.filter(item => item.department === selectedDepartment);
  }, [eventItems, selectedDepartment]);

  const filteredStockItems = useMemo(() => {
    const keyword = searchTerm.trim().toLowerCase();
    return selectedStockItems.filter(item => {
      if (!keyword) return true;
      return [item.name, item.unit, item.note, item.expiryDate]
        .filter(Boolean)
        .some(value => String(value).toLowerCase().includes(keyword));
    });
  }, [searchTerm, selectedStockItems]);

  const stockDepartmentSections = useMemo(() => {
    const keyword = searchTerm.trim();
    return departments
      .filter(department => selectedDepartment === '全部' || department === selectedDepartment)
      .map(department => {
        const allItems = selectedStockItems.filter(item => item.department === department);
        const visibleItems = filteredStockItems.filter(item => item.department === department);
        const expiredCount = allItems.filter(item => getExpiryStatus(item.expiryDate) === 'expired').length;
        return {
          department,
          allItems,
          visibleItems,
          expiredCount,
        };
      })
      .filter(section => keyword ? section.visibleItems.length > 0 : section.allItems.length > 0);
  }, [departments, filteredStockItems, searchTerm, selectedDepartment, selectedStockItems]);

  const stockSummary = useMemo(() => ({
    itemCount: selectedStockItems.length,
    expiredCount: selectedStockItems.filter(item => getExpiryStatus(item.expiryDate) === 'expired').length,
  }), [selectedStockItems]);

  const departmentRequests = useMemo(() =>
    requests.filter(request =>
      matchesSelectedInventoryEvent(request, selectedEventValue, selectedEventLabel) &&
      (selectedDepartment === '全部' || request.department === selectedDepartment)
    ),
  [requests, selectedDepartment, selectedEventLabel, selectedEventValue]);

  const autoReplenishmentProposals = useMemo(() =>
    buildAutoReplenishmentProposals(
      items,
      requests,
      selectedEventValue,
      selectedEventLabel,
      selectedDepartment
    ),
  [items, requests, selectedDepartment, selectedEventLabel, selectedEventValue]);

  const selectedAutoProposals = useMemo(
    () => autoReplenishmentProposals.filter(proposal => selectedAutoProposalIds[proposal.id]),
    [autoReplenishmentProposals, selectedAutoProposalIds]
  );

  const autoProposalSummary = useMemo(() => ({
    total: autoReplenishmentProposals.length,
    expired: autoReplenishmentProposals.filter(proposal => proposal.reason === 'expired').length,
    lowStock: autoReplenishmentProposals.filter(proposal => proposal.reason === 'low_stock').length,
    zeroStock: autoReplenishmentProposals.filter(proposal => proposal.reason === 'zero_stock').length,
  }), [autoReplenishmentProposals]);

  useEffect(() => {
    setSelectedAutoProposalIds(prev => {
      const validIds = new Set(autoReplenishmentProposals.map(proposal => proposal.id));
      const next = Object.fromEntries(
        Object.entries(prev).filter(([id, selected]) => selected && validIds.has(id))
      );
      return next;
    });
  }, [autoReplenishmentProposals]);

  useEffect(() => {
    setRequestQuantityDrafts(prev => {
      const next = { ...prev };
      requests.forEach(request => {
        request.items.forEach(line => {
          const key = `${request.id}:${line.id}`;
          if (next[key] === undefined) {
            next[key] = line.requestedQuantity === undefined ? '' : String(line.requestedQuantity);
          }
        });
      });
      return next;
    });
  }, [requests]);

  const visibleLogs = useMemo(() =>
    logs.filter(log =>
      matchesSelectedInventoryEvent(log, selectedEventValue, selectedEventLabel) &&
      (selectedDepartment === '全部' || log.department === selectedDepartment)
    ),
  [logs, selectedDepartment, selectedEventLabel, selectedEventValue]);

  const buildInventoryPrintHtml = (
    title: string,
    printItems: InventoryItem[],
    printDepartments: string[]
  ) => {
    const generatedAt = formatPrintDateTime();
    const eventLabel = selectedEventLabel || '未指定梯次';
    const departmentsHtml = printDepartments.map(department => {
      const departmentItems = printItems.filter(item => item.department === department);
      const rowsHtml = departmentItems.length > 0
        ? departmentItems.map((item, index) => `
            <tr>
              <td>${index + 1}</td>
              <td>${escapeHtml(item.name)}</td>
              <td class="number">${escapeHtml(item.quantity)}</td>
              <td>${escapeHtml(item.unit)}</td>
              <td>${escapeHtml(item.expiryDate || '')}</td>
              <td>${escapeHtml(item.note || '')}</td>
            </tr>
          `).join('')
        : '<tr><td colspan="6" class="empty">目前沒有品項</td></tr>';

      return `
        <section>
          <h2>${escapeHtml(department)}</h2>
          <table>
            <thead>
              <tr>
                <th class="index">#</th>
                <th>品項</th>
                <th class="number">數量</th>
                <th>單位</th>
                <th>效期</th>
                <th>備註</th>
              </tr>
            </thead>
            <tbody>${rowsHtml}</tbody>
          </table>
        </section>
      `;
    }).join('');

    return `<!doctype html>
      <html lang="zh-Hant">
        <head>
          <meta charset="UTF-8" />
          <title>${escapeHtml(title)}</title>
          <style>
            @page { size: A4; margin: 14mm; }
            body {
              color: #111827;
              font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", "Noto Sans TC", "Microsoft JhengHei", sans-serif;
              font-size: 12px;
              line-height: 1.5;
            }
            header {
              border-bottom: 2px solid #111827;
              margin-bottom: 14px;
              padding-bottom: 10px;
            }
            h1 {
              font-size: 22px;
              margin: 0 0 6px;
            }
            h2 {
              background: #f3f4f6;
              border: 1px solid #d1d5db;
              font-size: 16px;
              margin: 18px 0 0;
              padding: 8px 10px;
            }
            .meta {
              color: #4b5563;
              display: flex;
              flex-wrap: wrap;
              gap: 12px;
            }
            table {
              border-collapse: collapse;
              margin-bottom: 12px;
              table-layout: fixed;
              width: 100%;
            }
            th, td {
              border: 1px solid #d1d5db;
              padding: 7px 8px;
              text-align: left;
              vertical-align: top;
              word-break: break-word;
            }
            th {
              background: #f9fafb;
              font-weight: 800;
            }
            .index {
              width: 32px;
            }
            .number {
              text-align: right;
              width: 58px;
            }
            .empty {
              color: #6b7280;
              text-align: center;
            }
            section {
              break-inside: avoid;
            }
            @media print {
              body { print-color-adjust: exact; -webkit-print-color-adjust: exact; }
            }
          </style>
        </head>
        <body>
          <header>
            <h1>${escapeHtml(title)}</h1>
            <div class="meta">
              <span>系統年度：${escapeHtml(systemYear)}</span>
              <span>對應梯次：${escapeHtml(eventLabel)}</span>
              <span>列印時間：${escapeHtml(generatedAt)}</span>
              <span>列印人：${escapeHtml(currentUserLabel)}</span>
            </div>
          </header>
          ${departmentsHtml}
          <script>
            window.addEventListener('load', () => {
              window.focus();
              window.print();
            });
          </script>
        </body>
      </html>`;
  };

  const printInventoryList = (mode: 'department' | 'all', departmentToPrint = selectedDepartment) => {
    const printDepartments = mode === 'department'
      ? [departmentToPrint]
      : departments.filter(department => department !== '全部');
    const printItems = mode === 'department'
      ? eventItems.filter(item => item.department === departmentToPrint)
      : eventItems;
    const title = mode === 'department'
      ? `${departmentToPrint}最新庫存清單`
      : '各科最新庫存清單';

    const printWindow = window.open('', '_blank');
    if (!printWindow) {
      showToast('error', '無法開啟列印視窗', '請允許瀏覽器彈出視窗後再試。');
      return;
    }

    printWindow.document.open();
    printWindow.document.write(buildInventoryPrintHtml(title, printItems, printDepartments));
    printWindow.document.close();
  };

  const updateDraft = (id: string, patch: Partial<InventoryDraft>) => {
    setDrafts(prev => ({
      ...prev,
      [id]: {
        ...prev[id],
        ...patch,
      },
    }));
  };

  const normalizeDraft = (draft: InventoryDraft, existing?: InventoryItem): Omit<InventoryItem, 'id'> & { id?: string } => {
    const name = sanitizeText(draft.name, 120);
    const unit = sanitizeText(draft.unit, 20);
    const note = sanitizeText(draft.note, 300);
    const quantity = Number(draft.quantity);

    if (!name) throw new Error('請輸入品項名稱');
    if (!unit) throw new Error('請輸入單位');
    if (!Number.isFinite(quantity) || quantity < 0) throw new Error('數量不可小於 0');

    return {
      ...existing,
      id: draft.id,
      department: draft.department,
      name,
      quantity,
      unit,
      expiryDate: draft.expiryDate || undefined,
      note: note || undefined,
      eventId: selectedEventValue || undefined,
      eventLabel: selectedEventLabel || undefined,
    };
  };

  const handleSaveItem = async (draft: InventoryDraft, existing?: InventoryItem) => {
    setIsSavingId(draft.id || 'new');
    try {
      const nextItem = normalizeDraft(draft, existing);
      const saved = await saveInventoryItem(nextItem, currentUserLabel, existing);
      setDrafts(prev => ({ ...prev, [saved.id]: makeDraftFromItem(saved) }));
      if (!existing) {
        setNewItemDraft(makeEmptyDraft(draft.department || DEFAULT_DEPARTMENTS[0]));
        setIsNewItemOpen(false);
      }
      showToast('success', existing ? '庫存已更新' : '品項已新增', saved.name);
    } catch (error) {
      console.error(error);
      showToast('error', '儲存失敗', error instanceof Error ? error.message : '請檢查輸入內容');
    } finally {
      setIsSavingId(null);
    }
  };

  const handleDeleteItem = async (item: InventoryItem) => {
    if (!window.confirm(`確定要刪除「${item.name}」？修改紀錄仍會保留。`)) return;

    setIsSavingId(item.id);
    try {
      await deleteInventoryItem({
        ...item,
        eventId: selectedEventValue || item.eventId,
        eventLabel: selectedEventLabel || item.eventLabel
      }, currentUserLabel);
      showToast('success', '品項已刪除', item.name);
    } catch (error) {
      console.error(error);
      showToast('error', '刪除失敗', '無法刪除此品項');
    } finally {
      setIsSavingId(null);
    }
  };

  const handleCopyFromPreviousEvent = async () => {
    if (!previousEventWithItems || !selectedEventValue) return;

    setIsCopyingPrevious(true);
    try {
      await copyInventoryItemsFromEvent(
        previousEventWithItems.items,
        selectedEventValue,
        selectedEventLabel,
        currentUserLabel
      );
      showToast(
        'success',
        '已帶入上一梯資料',
        `已複製「${previousEventWithItems.event.label}」共 ${previousEventWithItems.items.length} 項物資，請依實際狀況調整`
      );
    } catch (error) {
      console.error(error);
      showToast('error', '帶入失敗', '無法複製上一梯物資資料');
    } finally {
      setIsCopyingPrevious(false);
    }
  };

  const handleCreateRequest = async () => {
    if (!requestDraft) return;
    const requestedQuantity = Number(requestDraft.requestedQuantity);
    if (!Number.isFinite(requestedQuantity) || requestedQuantity <= 0) {
      showToast('error', '數量不正確', '申請補貨數量必須大於 0');
      return;
    }

    setIsSavingId(`request-${requestDraft.item.id}`);
    try {
      await createReplenishmentRequest({
        department: requestDraft.item.department,
        eventId: selectedEventValue || undefined,
        eventLabel: selectedEventLabel || undefined,
        source: 'manual',
        items: [{
          id: `${requestDraft.item.id}-${Date.now()}`,
          inventoryItemId: requestDraft.item.id,
          name: requestDraft.item.name,
          currentQuantity: requestDraft.item.quantity,
          requestedQuantity,
          unit: requestDraft.item.unit,
          expiryDate: requestDraft.item.expiryDate,
          note: sanitizeText(requestDraft.note, 300) || undefined,
        }],
      }, currentUserLabel);

      setRequestDraft(null);
      setActiveTab('requests');
      showToast('success', '補貨單已送出', requestDraft.item.name);
    } catch (error) {
      console.error(error);
      showToast('error', '送出失敗', '無法建立補貨單');
    } finally {
      setIsSavingId(null);
    }
  };

  const handleUpdateRequestStatus = async (request: ReplenishmentRequest, status: InventoryRequestStatus) => {
    setIsSavingId(request.id);
    try {
      await updateReplenishmentRequestStatus(request, status, currentUserLabel);
      showToast('success', '補貨單已更新', getStatusLabel(status));
    } catch (error) {
      console.error(error);
      showToast('error', '更新失敗', '無法更新補貨單狀態');
    } finally {
      setIsSavingId(null);
    }
  };

  const makeAutoRequestLine = (proposal: AutoReplenishmentProposal) => ({
    id: `${proposal.itemIds[0] || proposal.name}-${Date.now()}`,
    inventoryItemId: proposal.itemIds[0],
    inventoryItemIds: proposal.itemIds,
    name: proposal.name,
    currentQuantity: proposal.availableQuantity,
    requestedQuantity: proposal.requestedQuantity,
    unit: proposal.unit,
    expiryBreakdown: proposal.expiryBreakdown,
    minimumQuantity: proposal.minimumQuantity,
    targetQuantity: proposal.targetQuantity,
    autoReason: proposal.reasonLabel,
    note: proposal.note,
  });

  const handleToggleAutoProposal = (proposalId: string, selected: boolean) => {
    setSelectedAutoProposalIds(prev => ({
      ...prev,
      [proposalId]: selected,
    }));
  };

  const handleToggleAllAutoProposals = (selected: boolean) => {
    setSelectedAutoProposalIds(
      selected
        ? Object.fromEntries(autoReplenishmentProposals.map(proposal => [proposal.id, true]))
        : {}
    );
  };

  const handleCreateSelectedAutoRequests = async () => {
    if (selectedAutoProposals.length === 0) {
      showToast('info', '尚未選取品項', '請先勾選要建立補貨單的試算項目');
      return;
    }

    const confirmed = window.confirm(`確定要建立 ${selectedAutoProposals.length} 張補貨單嗎？`);
    if (!confirmed) return;

    setIsSavingId('auto-replenishment');
    try {
      for (const proposal of selectedAutoProposals) {
        await createReplenishmentRequest({
          department: proposal.department,
          eventId: proposal.eventId || selectedEventValue || undefined,
          eventLabel: proposal.eventLabel || selectedEventLabel || undefined,
          source: 'auto',
          autoReason: proposal.reasonLabel,
          autoRuleSummary: proposal.ruleSummary,
          note: `${proposal.categoryLabel}；${proposal.note}`,
          items: [makeAutoRequestLine(proposal)],
        }, currentUserLabel);
      }

      setSelectedAutoProposalIds({});
      setActiveTab('requests');
      showToast('success', '補貨單已建立', `已建立 ${selectedAutoProposals.length} 張補貨單`);
    } catch (error) {
      console.error(error);
      showToast('error', '建立失敗', '無法建立自動補貨單');
    } finally {
      setIsSavingId(null);
    }
  };

  const requestLineDraftKey = (requestId: string, lineId: string) => `${requestId}:${lineId}`;

  const formatRequestedQuantity = (quantity?: number) =>
    quantity === undefined ? '待決定' : String(quantity);

  const handleUpdateRequestLineQuantity = async (
    request: ReplenishmentRequest,
    lineId: string
  ) => {
    const draftKey = requestLineDraftKey(request.id, lineId);
    const rawValue = (requestQuantityDrafts[draftKey] || '').trim();
    const nextQuantity = rawValue === '' ? undefined : Number(rawValue);

    if (nextQuantity !== undefined && (!Number.isFinite(nextQuantity) || nextQuantity <= 0)) {
      showToast('error', '數量不正確', '申請數量必須大於 0，或留空表示待決定');
      return;
    }

    const currentLine = request.items.find(line => line.id === lineId);
    if (currentLine?.requestedQuantity === nextQuantity) {
      showToast('info', '數量未變更', '目前申請數量沒有變動');
      return;
    }

    const nextItems = request.items.map(line =>
      line.id === lineId
        ? {
          ...line,
          requestedQuantity: nextQuantity,
        }
        : line
    );

    setIsSavingId(`${request.id}:${lineId}`);
    try {
      await updateReplenishmentRequestItems(request, nextItems, currentUserLabel);
      showToast('success', '申請數量已更新', nextQuantity === undefined ? '待決定' : String(nextQuantity));
    } catch (error) {
      console.error(error);
      showToast('error', '更新失敗', '無法更新補貨單數量');
    } finally {
      setIsSavingId(null);
    }
  };

  const renderDepartmentSelect = (includeAll = false) => (
    <select
      value={selectedDepartment}
      onChange={(event) => setSelectedDepartment(event.target.value)}
      className="min-h-11 rounded-lg border-2 border-gray-200 bg-white px-3 py-2 text-sm font-bold text-gray-800 outline-none focus:border-indigo-500 focus:ring-2 focus:ring-indigo-100"
    >
      {includeAll && <option value="全部">全部科別</option>}
      {departments.map(department => (
        <option key={department} value={department}>{department}</option>
      ))}
    </select>
  );

  const toggleDepartment = (department: string) => {
    setOpenDepartments(prev => ({
      ...prev,
      [department]: !(prev[department] ?? true),
    }));
  };

  const toggleItem = (itemId: string) => {
    setExpandedItemIds(prev => ({
      ...prev,
      [itemId]: !prev[itemId],
    }));
  };

  const openNewItemForm = (department = DEFAULT_DEPARTMENTS[0]) => {
    setNewItemDraft(makeEmptyDraft(department));
    setIsNewItemOpen(true);
  };

  const selectedStockDepartmentForAction = selectedDepartment === '全部'
    ? departments[0] || DEFAULT_DEPARTMENTS[0]
    : selectedDepartment;

  const allAutoProposalsSelected = autoReplenishmentProposals.length > 0 &&
    selectedAutoProposals.length === autoReplenishmentProposals.length;

  const getAutoReasonClass = (reason: AutoReplenishmentProposal['reason']) => {
    if (reason === 'expired') return 'border-red-200 bg-red-50 text-red-700';
    if (reason === 'low_stock') return 'border-amber-200 bg-amber-50 text-amber-700';
    return 'border-blue-200 bg-blue-50 text-blue-700';
  };

  const renderExpiryStatus = (expiryDate?: string) => {
    const expiryBadge = getExpiryBadge(expiryDate);
    return expiryBadge ? (
      <span className={`inline-flex whitespace-nowrap rounded-full border px-2.5 py-1 text-xs font-black ${expiryBadge.className}`}>
        {expiryBadge.label}
      </span>
    ) : null;
  };

  const renderItemEditPanel = (item: InventoryItem, draft: InventoryDraft) => (
    <div className="grid gap-3 rounded-xl border border-gray-200 bg-gray-50 p-3 lg:grid-cols-[1.2fr_180px_180px_1fr_auto] lg:items-end">
      <label className="block">
        <span className="mb-1 block text-xs font-bold text-gray-500">品項</span>
        <input
          value={draft.name}
          onChange={(event) => updateDraft(item.id, { name: event.target.value })}
          className="min-h-11 w-full rounded-lg border border-gray-200 bg-white px-3 font-bold text-gray-900 outline-none focus:border-indigo-500 focus:ring-2 focus:ring-indigo-100"
        />
      </label>
      <div className="grid grid-cols-2 gap-2">
        <label>
          <span className="mb-1 block text-xs font-bold text-gray-500">數量</span>
          <input
            type="number"
            min="0"
            value={draft.quantity}
            onChange={(event) => updateDraft(item.id, { quantity: event.target.value })}
            className="min-h-11 w-full rounded-lg border border-gray-200 bg-white px-3 text-right font-black text-gray-900 outline-none focus:border-indigo-500 focus:ring-2 focus:ring-indigo-100"
          />
        </label>
        <label>
          <span className="mb-1 block text-xs font-bold text-gray-500">單位</span>
          <input
            list="inventory-unit-options"
            value={draft.unit}
            onChange={(event) => updateDraft(item.id, { unit: event.target.value })}
            className="min-h-11 w-full rounded-lg border border-gray-200 bg-white px-3 font-bold text-gray-900 outline-none focus:border-indigo-500 focus:ring-2 focus:ring-indigo-100"
          />
        </label>
      </div>
      <label className="block">
        <span className="mb-1 block text-xs font-bold text-gray-500">效期</span>
        <input
          type="date"
          value={draft.expiryDate}
          onChange={(event) => updateDraft(item.id, { expiryDate: event.target.value })}
          className="min-h-11 w-full rounded-lg border border-gray-200 bg-white px-3 text-sm font-bold text-gray-900 outline-none focus:border-indigo-500 focus:ring-2 focus:ring-indigo-100"
        />
      </label>
      <label className="block">
        <span className="mb-1 block text-xs font-bold text-gray-500">備註</span>
        <input
          value={draft.note}
          onChange={(event) => updateDraft(item.id, { note: event.target.value })}
          className="min-h-11 w-full rounded-lg border border-gray-200 bg-white px-3 text-gray-900 outline-none focus:border-indigo-500 focus:ring-2 focus:ring-indigo-100"
        />
      </label>
      <div className="grid grid-cols-3 gap-2 lg:w-[156px]">
        <button
          onClick={() => handleSaveItem(draft, item)}
          disabled={isSavingId === item.id}
          className="inline-flex h-11 items-center justify-center rounded-lg bg-indigo-600 text-white hover:bg-indigo-700 disabled:opacity-50"
          title="儲存"
        >
          <Save size={18} />
        </button>
        <button
          onClick={() => setRequestDraft({ item, requestedQuantity: '1', note: '' })}
          className="inline-flex h-11 items-center justify-center rounded-lg border border-blue-200 bg-white text-blue-600 hover:bg-blue-50"
          title="申請補貨"
        >
          <ShoppingCart size={18} />
        </button>
        <button
          onClick={() => handleDeleteItem(item)}
          disabled={isSavingId === item.id}
          className="inline-flex h-11 items-center justify-center rounded-lg border border-red-200 bg-white text-red-600 hover:bg-red-50 disabled:opacity-50"
          title="刪除"
        >
          <Trash2 size={18} />
        </button>
      </div>
      <div className="text-xs leading-5 text-gray-500 lg:col-span-5">
        <span className="font-black text-gray-700">最後更新：</span>
        {formatDateTime(item.updatedAt) || '尚未紀錄'}
        {item.updatedBy || item.createdBy ? ` / ${item.updatedBy || item.createdBy}` : ''}
      </div>
    </div>
  );

  return (
    <div className="space-y-5 animate-fade-in-up">
      <div className="flex flex-col xl:flex-row xl:items-center justify-between gap-4">
        <div>
          <h2 className="text-2xl md:text-3xl font-black text-gray-900">{systemYear} 年度物資管理</h2>
        </div>

        <div className="flex flex-col md:flex-row gap-3">
          {lockedEventId ? (
            <div className="flex min-h-11 items-center gap-2 rounded-lg border-2 border-indigo-200 bg-indigo-50 px-3 py-2 text-sm font-bold text-indigo-800">
              <Lock size={16} />
              {selectedInventoryEvent?.label || '本梯次'}
            </div>
          ) : (
            <select
              value={selectedEventValue}
              onChange={(event) => {
                setHasUserSelectedEvent(true);
                setSelectedEventId(event.target.value);
              }}
              className="min-h-11 rounded-lg border-2 border-gray-200 bg-white px-3 py-2 text-sm font-bold text-gray-800 outline-none focus:border-indigo-500 focus:ring-2 focus:ring-indigo-100"
            >
              <option value="">未指定梯次</option>
              {activeEventOptions.map(event => (
                <option key={event.id} value={event.id}>{event.label}</option>
              ))}
            </select>
          )}
          {renderDepartmentSelect(true)}
        </div>
      </div>

      {canManageAccessCodes && selectedEventValue && (
        <div className="rounded-xl border border-indigo-100 bg-indigo-50/60 p-4">
          <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
            <div>
              <h3 className="flex items-center gap-2 text-sm font-black text-indigo-900">
                <KeyRound size={16} />
                本梯現場清點通行碼
              </h3>
              <p className="mt-1 text-xs leading-5 text-indigo-700">
                現場同工不需要 Google 帳號，輸入這組碼即可只編輯「{selectedInventoryEvent?.label || '本梯次'}」的物資，權限 14 小時後自動失效。
              </p>
            </div>

            <div className="flex flex-wrap items-center gap-2">
              {accessCodeEntry?.code && accessCodeEntry.active !== false ? (
                <span className="rounded-lg border-2 border-indigo-300 bg-white px-4 py-2 font-mono text-lg font-black tracking-[0.3em] text-indigo-800">
                  {accessCodeEntry.code}
                </span>
              ) : (
                <span className="rounded-lg border-2 border-dashed border-indigo-200 px-4 py-2 text-sm font-bold text-indigo-400">
                  {accessCodeEntry?.code ? '已停用' : '尚未產生'}
                </span>
              )}

              <button
                onClick={handleGenerateAccessCode}
                disabled={isSavingAccessCode}
                className="flex min-h-11 items-center gap-2 rounded-lg bg-indigo-600 px-4 py-2 text-sm font-bold text-white transition-colors hover:bg-indigo-700 disabled:cursor-not-allowed disabled:bg-indigo-300"
              >
                <RotateCw size={16} />
                {accessCodeEntry?.code ? '重新產生' : '產生通行碼'}
              </button>

              {accessCodeEntry?.code && accessCodeEntry.active !== false && (
                <button
                  onClick={handleDisableAccessCode}
                  disabled={isSavingAccessCode}
                  className="flex min-h-11 items-center gap-2 rounded-lg border-2 border-red-200 px-4 py-2 text-sm font-bold text-red-600 transition-colors hover:bg-red-50 disabled:cursor-not-allowed disabled:opacity-50"
                >
                  <X size={16} />
                  停用
                </button>
              )}
            </div>
          </div>

          {accessCodeEntry?.code && accessCodeEntry.active !== false && (
            <div className="mt-4 border-t border-indigo-100 pt-4">
              <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                <p className="text-xs font-bold text-indigo-900">
                  傳給同工的訊息（含網址與操作步驟）
                </p>
                <button
                  onClick={handleCopyInvite}
                  className="flex min-h-11 items-center justify-center gap-2 rounded-lg border-2 border-indigo-300 bg-white px-4 py-2 text-sm font-bold text-indigo-700 transition-colors hover:bg-indigo-100"
                >
                  <Copy size={16} />
                  複製邀請訊息
                </button>
              </div>
              <pre className="mt-3 max-h-48 overflow-auto whitespace-pre-wrap rounded-lg border border-indigo-100 bg-white p-3 font-sans text-xs leading-6 text-gray-700 select-all">
                {inviteMessage}
              </pre>
            </div>
          )}
        </div>
      )}

      <div className="flex flex-wrap gap-2 rounded-xl border border-gray-200 bg-white p-1 shadow-sm">
        {[
          { id: 'stock', label: '庫存', icon: Package },
          { id: 'auto-replenishment', label: '自動補貨試算', icon: ClipboardList },
          { id: 'requests', label: '補貨單', icon: ShoppingCart },
          { id: 'logs', label: '修改紀錄', icon: History },
        ].map(tab => {
          const Icon = tab.icon;
          return (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id as InventoryTab)}
              className={`flex items-center gap-2 rounded-lg px-4 py-2.5 text-sm font-bold transition-colors ${
                activeTab === tab.id
                  ? 'bg-indigo-100 text-indigo-800'
                  : 'text-gray-600 hover:bg-gray-100'
              }`}
            >
              <Icon size={18} />
              {tab.label}
            </button>
          );
        })}
      </div>

      {activeTab === 'stock' && (
        <section className="rounded-2xl border border-gray-200 bg-white shadow-sm">
          <div className="flex flex-col gap-4 border-b border-gray-200 p-4 xl:flex-row xl:items-center xl:justify-between">
            <div className="flex items-center gap-3">
              <Package className="text-indigo-600" size={24} />
              <div>
                <h3 className="text-lg font-black text-gray-900">
                  {selectedDepartment === '全部' ? '全部科別庫存' : `${selectedDepartment}庫存`}
                </h3>
                <p className="text-xs text-gray-500">{selectedEventLabel || '未指定梯次'}</p>
              </div>
            </div>
            <div className="flex w-full flex-col gap-2 xl:w-auto xl:flex-row xl:items-center">
              <div className="relative w-full xl:w-80">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={18} />
                <input
                  type="search"
                  value={searchTerm}
                  onChange={(event) => setSearchTerm(event.target.value)}
                  placeholder="搜尋品項、單位、備註"
                  className="min-h-11 w-full rounded-lg border-2 border-gray-200 pl-10 pr-3 text-sm outline-none focus:border-indigo-500 focus:ring-2 focus:ring-indigo-100"
                />
              </div>
              <button
                type="button"
                onClick={() => openNewItemForm(selectedStockDepartmentForAction)}
                className="inline-flex min-h-11 items-center justify-center gap-2 rounded-lg bg-indigo-600 px-3 text-sm font-bold text-white hover:bg-indigo-700"
              >
                <Plus size={18} />
                新增品項
              </button>
              <button
                type="button"
                onClick={() => selectedDepartment === '全部'
                  ? printInventoryList('all')
                  : printInventoryList('department', selectedDepartment)
                }
                className="inline-flex min-h-11 items-center justify-center gap-2 rounded-lg border border-gray-200 bg-white px-3 text-sm font-bold text-gray-700 hover:bg-gray-50"
              >
                <Printer size={18} />
                {selectedDepartment === '全部' ? '列印全部科別' : '列印目前科別'}
              </button>
            </div>
          </div>

          <div className="grid gap-3 border-b border-gray-100 p-4 sm:grid-cols-2">
            <div className="rounded-lg border border-gray-200 bg-gray-50 px-4 py-3">
              <div className="text-xs font-black text-gray-500">品項</div>
              <div className="mt-1 text-2xl font-black text-gray-900">{stockSummary.itemCount}</div>
            </div>
            <div className="rounded-lg border border-red-100 bg-red-50 px-4 py-3">
              <div className="text-xs font-black text-red-600">已過期</div>
              <div className="mt-1 text-2xl font-black text-red-700">{stockSummary.expiredCount}</div>
            </div>
          </div>

          {!isLoading && eventItems.length === 0 && previousEventWithItems && (
            <div className="mx-4 mt-4 flex flex-col gap-3 rounded-xl border border-indigo-200 bg-indigo-50 p-4 sm:flex-row sm:items-center sm:justify-between">
              <div className="flex items-start gap-3">
                <History className="mt-0.5 shrink-0 text-indigo-600" size={20} />
                <div>
                  <p className="text-sm font-black text-indigo-950">
                    偵測到上一梯「{previousEventWithItems.event.label}」有 {previousEventWithItems.items.length} 項物資
                  </p>
                  <p className="mt-0.5 text-xs text-indigo-700">帶入後可直接依本梯次實際狀況調整品項與數量，不用重新輸入</p>
                </div>
              </div>
              <button
                type="button"
                onClick={handleCopyFromPreviousEvent}
                disabled={isCopyingPrevious}
                className="inline-flex min-h-11 shrink-0 items-center justify-center gap-2 rounded-lg bg-indigo-600 px-4 text-sm font-bold text-white hover:bg-indigo-700 disabled:opacity-50"
              >
                {isCopyingPrevious ? <RotateCw className="animate-spin" size={18} /> : <History size={18} />}
                帶入上一梯資料
              </button>
            </div>
          )}

          {isNewItemOpen && (
            <div className="mx-2 my-3 rounded-xl border border-indigo-200 bg-indigo-50/50 p-3 sm:m-4 sm:p-4">
              <div className="mb-3 flex items-center justify-between gap-3">
                <p className="text-sm font-black text-indigo-950">新增品項</p>
                <button
                  type="button"
                  onClick={() => setIsNewItemOpen(false)}
                  className="rounded-lg p-2 text-indigo-700 hover:bg-indigo-100"
                  title="關閉"
                >
                  <X size={18} />
                </button>
              </div>
              <div className="grid gap-3 lg:grid-cols-[160px_1.2fr_120px_120px_170px_1fr_auto] lg:items-end">
                <label className="block">
                  <span className="mb-1 block text-xs font-bold text-indigo-700">科別</span>
                  <select
                    value={newItemDraft.department}
                    onChange={(event) => setNewItemDraft(prev => ({ ...prev, department: event.target.value }))}
                    className="min-h-11 w-full rounded-lg border border-indigo-200 bg-white px-3 text-sm font-bold text-gray-900 outline-none focus:border-indigo-500 focus:ring-2 focus:ring-indigo-100"
                  >
                    {departments.map(department => (
                      <option key={department} value={department}>{department}</option>
                    ))}
                  </select>
                </label>
                <label className="block">
                  <span className="mb-1 block text-xs font-bold text-indigo-700">品項</span>
                  <input
                    value={newItemDraft.name}
                    onChange={(event) => setNewItemDraft(prev => ({ ...prev, name: event.target.value }))}
                    className="min-h-11 w-full rounded-lg border border-indigo-200 bg-white px-3 font-bold text-gray-900 outline-none focus:border-indigo-500 focus:ring-2 focus:ring-indigo-100"
                    placeholder="品項名稱"
                  />
                </label>
                <label className="block">
                  <span className="mb-1 block text-xs font-bold text-indigo-700">數量</span>
                  <input
                    type="number"
                    min="0"
                    value={newItemDraft.quantity}
                    onChange={(event) => setNewItemDraft(prev => ({ ...prev, quantity: event.target.value }))}
                    className="min-h-11 w-full rounded-lg border border-indigo-200 bg-white px-3 text-right font-black text-gray-900 outline-none focus:border-indigo-500 focus:ring-2 focus:ring-indigo-100"
                  />
                </label>
                <label className="block">
                  <span className="mb-1 block text-xs font-bold text-indigo-700">單位</span>
                  <input
                    list="inventory-unit-options"
                    value={newItemDraft.unit}
                    onChange={(event) => setNewItemDraft(prev => ({ ...prev, unit: event.target.value }))}
                    className="min-h-11 w-full rounded-lg border border-indigo-200 bg-white px-3 font-bold text-gray-900 outline-none focus:border-indigo-500 focus:ring-2 focus:ring-indigo-100"
                  />
                </label>
                <label className="block">
                  <span className="mb-1 block text-xs font-bold text-indigo-700">效期</span>
                  <input
                    type="date"
                    value={newItemDraft.expiryDate}
                    onChange={(event) => setNewItemDraft(prev => ({ ...prev, expiryDate: event.target.value }))}
                    className="min-h-11 w-full rounded-lg border border-indigo-200 bg-white px-3 text-sm font-bold text-gray-900 outline-none focus:border-indigo-500 focus:ring-2 focus:ring-indigo-100"
                  />
                </label>
                <label className="block">
                  <span className="mb-1 block text-xs font-bold text-indigo-700">備註</span>
                  <input
                    value={newItemDraft.note}
                    onChange={(event) => setNewItemDraft(prev => ({ ...prev, note: event.target.value }))}
                    className="min-h-11 w-full rounded-lg border border-indigo-200 bg-white px-3 text-gray-900 outline-none focus:border-indigo-500 focus:ring-2 focus:ring-indigo-100"
                    placeholder="備註"
                  />
                </label>
                <button
                  onClick={() => handleSaveItem(newItemDraft)}
                  disabled={isSavingId === 'new'}
                  className="inline-flex min-h-11 items-center justify-center gap-2 rounded-lg bg-indigo-600 px-4 text-sm font-bold text-white hover:bg-indigo-700 disabled:opacity-50"
                >
                  <Plus size={18} />
                  新增
                </button>
              </div>
            </div>
          )}

          {requestDraft && (
            <div className="m-4 rounded-xl border border-blue-200 bg-blue-50 p-4">
              <div className="mb-3 flex items-start justify-between gap-3">
                <div>
                  <p className="text-sm font-bold text-blue-900">補貨申請：{requestDraft.item.name}</p>
                  <p className="text-xs text-blue-700">目前數量 {requestDraft.item.quantity} {requestDraft.item.unit}</p>
                </div>
                <button
                  onClick={() => setRequestDraft(null)}
                  className="rounded-lg p-2 text-blue-700 hover:bg-blue-100"
                  title="取消"
                >
                  <X size={18} />
                </button>
              </div>
              <div className="grid gap-3 md:grid-cols-[160px_1fr_auto]">
                <input
                  type="number"
                  min="1"
                  value={requestDraft.requestedQuantity}
                  onChange={(event) => setRequestDraft(prev => prev ? { ...prev, requestedQuantity: event.target.value } : prev)}
                  className="min-h-11 rounded-lg border-2 border-blue-200 px-3 text-right font-black outline-none focus:border-blue-500"
                  placeholder="補貨數量"
                />
                <input
                  value={requestDraft.note}
                  onChange={(event) => setRequestDraft(prev => prev ? { ...prev, note: event.target.value } : prev)}
                  className="min-h-11 rounded-lg border-2 border-blue-200 px-3 outline-none focus:border-blue-500"
                  placeholder="備註"
                />
                <button
                  onClick={handleCreateRequest}
                  disabled={isSavingId === `request-${requestDraft.item.id}`}
                  className="inline-flex min-h-11 items-center justify-center gap-2 rounded-lg bg-blue-600 px-4 text-sm font-bold text-white hover:bg-blue-700 disabled:opacity-50"
                >
                  <ClipboardList size={18} />
                  送出
                </button>
              </div>
            </div>
          )}

          <div className="divide-y divide-gray-100">
            {isLoading ? (
              <div className="px-4 py-10 text-center text-gray-500">
                <RotateCw className="mx-auto mb-2 animate-spin text-indigo-500" size={28} />
                載入物資庫存...
              </div>
            ) : stockDepartmentSections.length === 0 ? (
              <div className="px-4 py-10 text-center text-gray-500">
                {selectedDepartment === '全部' ? '目前梯次沒有符合的品項' : `目前梯次的${selectedDepartment}沒有符合品項`}
              </div>
            ) : (
              stockDepartmentSections.map(section => {
                const isDepartmentOpen = openDepartments[section.department] ?? true;
                return (
                  <div key={section.department}>
                    <div className="flex flex-col gap-3 p-4 lg:flex-row lg:items-center lg:justify-between">
                      <button
                        type="button"
                        onClick={() => toggleDepartment(section.department)}
                        className="flex min-w-0 items-center gap-3 text-left"
                      >
                        <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-gray-100 text-gray-600">
                          {isDepartmentOpen ? <ChevronDown size={18} /> : <ChevronRight size={18} />}
                        </span>
                        <span className="min-w-0">
                          <span className="block text-base font-black text-gray-900">{section.department}</span>
                          <span className="mt-1 flex flex-wrap gap-2 text-xs font-bold text-gray-500">
                            <span>{section.allItems.length} 品項</span>
                            {section.expiredCount > 0 && <span className="text-red-700">已過期 {section.expiredCount}</span>}
                          </span>
                        </span>
                      </button>
                      <div className="flex flex-wrap gap-2 lg:justify-end">
                        <button
                          type="button"
                          onClick={() => printInventoryList('department', section.department)}
                          className="inline-flex min-h-10 items-center justify-center gap-2 rounded-lg border border-gray-200 bg-white px-3 text-sm font-bold text-gray-700 hover:bg-gray-50"
                        >
                          <Printer size={17} />
                          列印
                        </button>
                        <button
                          type="button"
                          onClick={() => openNewItemForm(section.department)}
                          className="inline-flex min-h-10 items-center justify-center gap-2 rounded-lg border border-indigo-200 bg-white px-3 text-sm font-bold text-indigo-700 hover:bg-indigo-50"
                        >
                          <Plus size={17} />
                          新增
                        </button>
                      </div>
                    </div>

                    {isDepartmentOpen && (
                      <div className="border-t border-gray-100">
                        <div className="lg:hidden">
                          {section.visibleItems.map(item => {
                            const draft = drafts[item.id] || makeDraftFromItem(item);
                            const isExpanded = Boolean(expandedItemIds[item.id]);
                            return (
                              <article key={item.id} className="border-b border-gray-100 p-4 last:border-b-0">
                                <button
                                  type="button"
                                  onClick={() => toggleItem(item.id)}
                                  className="flex w-full items-start justify-between gap-3 text-left"
                                >
                                  <span className="min-w-0">
                                    <span className="block truncate text-base font-black text-gray-900">{draft.name}</span>
                                    <span className="mt-2 grid gap-1 text-xs font-bold text-gray-600 sm:grid-cols-3">
                                      <span className="min-w-0">
                                        <span className="mr-1 text-gray-400">數量</span>
                                        <span>{draft.quantity} {draft.unit}</span>
                                      </span>
                                      <span className="min-w-0">
                                        <span className="mr-1 text-gray-400">效期</span>
                                        <span>{formatExpiryDate(draft.expiryDate)}</span>
                                      </span>
                                      {draft.note && (
                                        <span className="min-w-0 truncate">
                                          <span className="mr-1 text-gray-400">備註</span>
                                          {draft.note}
                                        </span>
                                      )}
                                    </span>
                                  </span>
                                  <span className="flex shrink-0 flex-col items-end gap-2">
                                    {renderExpiryStatus(draft.expiryDate)}
                                    {Number(draft.quantity) <= 0 && <span className="rounded-full bg-gray-100 px-2 py-1 text-xs font-black text-gray-700">無庫存</span>}
                                  </span>
                                </button>
                                {isExpanded && <div className="mt-3">{renderItemEditPanel(item, draft)}</div>}
                              </article>
                            );
                          })}
                        </div>

                        <div className="hidden overflow-x-auto lg:block">
                          <table className="w-full min-w-[940px] table-fixed">
                            <colgroup>
                              <col className="w-[28%]" />
                              <col className="w-[11%]" />
                              <col className="w-[15%]" />
                              <col className="w-[14%]" />
                              <col className="w-[22%]" />
                              <col className="w-[10%]" />
                            </colgroup>
                            <thead className="bg-gray-50 text-left text-xs font-black uppercase tracking-wide text-gray-500">
                              <tr>
                                <th className="whitespace-nowrap px-4 py-3">品項</th>
                                <th className="whitespace-nowrap px-4 py-3 text-right">數量</th>
                                <th className="whitespace-nowrap px-4 py-3">效期</th>
                                <th className="whitespace-nowrap px-4 py-3">效期狀態</th>
                                <th className="whitespace-nowrap px-4 py-3">備註</th>
                                <th className="whitespace-nowrap px-4 py-3 text-right">操作</th>
                              </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-100">
                              {section.visibleItems.map(item => {
                                const draft = drafts[item.id] || makeDraftFromItem(item);
                                const isExpanded = Boolean(expandedItemIds[item.id]);
                                return (
                                  <React.Fragment key={item.id}>
                                    <tr className="align-middle hover:bg-gray-50/70">
                                      <td className="px-4 py-3 font-bold text-gray-900">{draft.name}</td>
                                      <td className="px-4 py-3 text-right">
                                        <span className="font-black text-gray-900">{draft.quantity}</span>
                                        <span className="ml-1 text-sm text-gray-500">{draft.unit}</span>
                                      </td>
                                      <td className="px-4 py-3 text-sm font-bold text-gray-700">{formatExpiryDate(draft.expiryDate)}</td>
                                      <td className="px-4 py-3">{renderExpiryStatus(draft.expiryDate)}</td>
                                      <td className="px-4 py-3 text-sm text-gray-600">{draft.note || ''}</td>
                                      <td className="px-4 py-3 text-right">
                                        <button
                                          type="button"
                                          onClick={() => toggleItem(item.id)}
                                          className="inline-flex h-10 w-10 items-center justify-center rounded-lg border border-gray-200 bg-white text-gray-700 hover:bg-gray-50"
                                          title="編輯"
                                        >
                                          {isExpanded ? <ChevronDown size={18} /> : <Pencil size={18} />}
                                        </button>
                                      </td>
                                    </tr>
                                    {isExpanded && (
                                      <tr>
                                        <td colSpan={6} className="bg-gray-50/60 px-4 py-3">
                                          {renderItemEditPanel(item, draft)}
                                        </td>
                                      </tr>
                                    )}
                                  </React.Fragment>
                                );
                              })}
                            </tbody>
                          </table>
                        </div>
                      </div>
                    )}
                  </div>
                );
              })
            )}
          </div>

          <datalist id="inventory-unit-options">
            {DEFAULT_UNITS.map(unit => <option key={unit} value={unit} />)}
          </datalist>
        </section>
      )}

      {activeTab === 'auto-replenishment' && (
        <section className="rounded-2xl border border-gray-200 bg-white shadow-sm">
          <div className="flex flex-col gap-4 border-b border-gray-200 p-4 xl:flex-row xl:items-center xl:justify-between">
            <div className="flex items-center gap-3">
              <ClipboardList className="text-indigo-600" size={24} />
              <div>
                <h3 className="text-lg font-black text-gray-900">自動補貨試算</h3>
                <p className="text-xs text-gray-500">{selectedEventLabel || '未指定梯次'}</p>
              </div>
            </div>
            <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
              <button
                type="button"
                onClick={() => handleToggleAllAutoProposals(!allAutoProposalsSelected)}
                disabled={autoReplenishmentProposals.length === 0}
                className="inline-flex min-h-10 items-center justify-center gap-2 rounded-lg border border-gray-200 bg-white px-3 text-sm font-bold text-gray-700 hover:bg-gray-50 disabled:opacity-50"
              >
                {allAutoProposalsSelected ? '取消全選' : '全選'}
              </button>
              <button
                type="button"
                onClick={handleCreateSelectedAutoRequests}
                disabled={selectedAutoProposals.length === 0 || isSavingId === 'auto-replenishment'}
                className="inline-flex min-h-10 items-center justify-center gap-2 rounded-lg bg-indigo-600 px-4 text-sm font-bold text-white hover:bg-indigo-700 disabled:opacity-50"
              >
                <Plus size={18} />
                建立補貨單
              </button>
            </div>
          </div>

          <div className="grid gap-3 border-b border-gray-100 p-4 sm:grid-cols-2 lg:grid-cols-4">
            <div className="rounded-lg border border-gray-200 bg-gray-50 px-4 py-3">
              <div className="text-xs font-black text-gray-500">試算項目</div>
              <div className="mt-1 text-2xl font-black text-gray-900">{autoProposalSummary.total}</div>
            </div>
            <div className="rounded-lg border border-red-100 bg-red-50 px-4 py-3">
              <div className="text-xs font-black text-red-600">過期</div>
              <div className="mt-1 text-2xl font-black text-red-700">{autoProposalSummary.expired}</div>
            </div>
            <div className="rounded-lg border border-amber-100 bg-amber-50 px-4 py-3">
              <div className="text-xs font-black text-amber-600">低庫存</div>
              <div className="mt-1 text-2xl font-black text-amber-700">{autoProposalSummary.lowStock}</div>
            </div>
            <div className="rounded-lg border border-blue-100 bg-blue-50 px-4 py-3">
              <div className="text-xs font-black text-blue-600">零庫存</div>
              <div className="mt-1 text-2xl font-black text-blue-700">{autoProposalSummary.zeroStock}</div>
            </div>
          </div>

          {autoReplenishmentProposals.length === 0 ? (
            <div className="p-10 text-center text-gray-500">目前沒有需要建立補貨單的試算項目</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full min-w-[980px] text-sm">
                <thead className="bg-gray-50 text-left text-xs font-black text-gray-500">
                  <tr>
                    <th className="w-12 px-4 py-3">
                      <input
                        type="checkbox"
                        checked={allAutoProposalsSelected}
                        onChange={(event) => handleToggleAllAutoProposals(event.target.checked)}
                        className="h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                      />
                    </th>
                    <th className="px-3 py-3">科別</th>
                    <th className="px-3 py-3">品項</th>
                    <th className="px-3 py-3">原因</th>
                    <th className="px-3 py-3 text-right">可用庫存</th>
                    <th className="px-3 py-3 text-right">建議申請</th>
                    <th className="px-3 py-3">規則</th>
                    <th className="px-3 py-3">效期明細</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {autoReplenishmentProposals.map(proposal => (
                    <tr key={proposal.id} className="align-top hover:bg-gray-50/70">
                      <td className="px-4 py-3">
                        <input
                          type="checkbox"
                          checked={Boolean(selectedAutoProposalIds[proposal.id])}
                          onChange={(event) => handleToggleAutoProposal(proposal.id, event.target.checked)}
                          className="h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                        />
                      </td>
                      <td className="px-3 py-3 font-bold text-gray-700">{proposal.department}</td>
                      <td className="px-3 py-3">
                        <div className="font-black text-gray-900">{proposal.name}</div>
                        <div className="mt-1 text-xs text-gray-500">{proposal.categoryLabel}</div>
                      </td>
                      <td className="px-3 py-3">
                        <span className={`inline-flex rounded-full border px-2.5 py-1 text-xs font-black ${getAutoReasonClass(proposal.reason)}`}>
                          {proposal.reasonLabel}
                        </span>
                      </td>
                      <td className="px-3 py-3 text-right">
                        <div className="font-black text-gray-900">{proposal.availableQuantity} {proposal.unit}</div>
                        {proposal.expiredQuantity > 0 && (
                          <div className="mt-1 text-xs font-bold text-red-600">過期 {proposal.expiredQuantity} {proposal.unit}</div>
                        )}
                      </td>
                      <td className="px-3 py-3 text-right font-black text-blue-700">
                        {proposal.requestedQuantity === undefined ? '待決定' : `${proposal.requestedQuantity} ${proposal.unit}`}
                      </td>
                      <td className="px-3 py-3 text-xs leading-5 text-gray-600">
                        {proposal.ruleSummary}
                      </td>
                      <td className="px-3 py-3 text-xs leading-5 text-gray-500">
                        {proposal.expiryBreakdown}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </section>
      )}

      {activeTab === 'requests' && (
        <section className="rounded-2xl border border-gray-200 bg-white shadow-sm">
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-3 border-b border-gray-200 p-4">
            <div className="flex items-center gap-3">
              <ShoppingCart className="text-blue-600" size={24} />
              <div>
                <h3 className="text-lg font-black text-gray-900">補貨單</h3>
              </div>
            </div>
            <button
              onClick={() => window.print()}
              className="inline-flex min-h-10 items-center justify-center gap-2 rounded-lg border border-gray-200 bg-white px-3 text-sm font-bold text-gray-700 hover:bg-gray-50"
            >
              <Printer size={18} />
              列印
            </button>
          </div>

          <div className="divide-y divide-gray-100">
            {departmentRequests.length === 0 ? (
              <div className="p-10 text-center text-gray-500">目前沒有補貨單</div>
            ) : departmentRequests.map(request => (
              <div key={request.id} className="p-4 hover:bg-gray-50/70">
                <div className="mb-3 flex flex-col lg:flex-row lg:items-start justify-between gap-3">
                  <div>
                    <div className="flex flex-wrap items-center gap-2">
                      <span className="text-base font-black text-gray-900">{request.department}</span>
                      <span className={`rounded-full border px-2.5 py-1 text-xs font-black ${getStatusClass(request.status)}`}>
                        {getStatusLabel(request.status)}
                      </span>
                      {request.source && (
                        <span className="rounded-full bg-gray-100 px-2.5 py-1 text-xs font-bold text-gray-700">
                          {request.source === 'auto' ? '自動試算' : '人工提出'}
                        </span>
                      )}
                      {request.autoReason && (
                        <span className="rounded-full bg-indigo-50 px-2.5 py-1 text-xs font-bold text-indigo-700">
                          {request.autoReason}
                        </span>
                      )}
                      {request.eventLabel && (
                        <span className="rounded-full bg-indigo-50 px-2.5 py-1 text-xs font-bold text-indigo-700">{request.eventLabel}</span>
                      )}
                    </div>
                    <p className="mt-1 text-xs text-gray-500">
                      {formatDateTime(request.requestedAt)} 由 {request.requestedBy} 送出
                      {request.quantityEditedBy && ` / ${formatDateTime(request.quantityEditedAt)} 由 ${request.quantityEditedBy} 修改數量`}
                    </p>
                    {request.note && <p className="mt-2 text-sm text-gray-600">{request.note}</p>}
                  </div>
                  {request.status === 'submitted' && (
                    <div className="flex gap-2">
                      <button
                        onClick={() => handleUpdateRequestStatus(request, 'handled')}
                        disabled={isSavingId === request.id}
                        className="inline-flex h-10 items-center gap-2 rounded-lg bg-green-600 px-3 text-sm font-bold text-white hover:bg-green-700 disabled:opacity-50"
                      >
                        <CheckCircle size={18} />
                        已處理
                      </button>
                      <button
                        onClick={() => handleUpdateRequestStatus(request, 'cancelled')}
                        disabled={isSavingId === request.id}
                        className="inline-flex h-10 items-center gap-2 rounded-lg border border-gray-200 bg-white px-3 text-sm font-bold text-gray-700 hover:bg-gray-50 disabled:opacity-50"
                      >
                        <X size={18} />
                        取消
                      </button>
                    </div>
                  )}
                </div>

                <div className="overflow-x-auto rounded-xl border border-gray-200">
                  <table className="w-full min-w-[860px] text-sm">
                    <thead className="bg-gray-50 text-left text-xs font-black text-gray-500">
                      <tr>
                        <th className="px-3 py-2">品項</th>
                        <th className="px-3 py-2 text-right">目前數量</th>
                        <th className="px-3 py-2 text-right">申請數量</th>
                        <th className="px-3 py-2">單位</th>
                        <th className="px-3 py-2">備註</th>
                        <th className="px-3 py-2 text-right">修改</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100">
                      {request.items.map(line => (
                        <tr key={line.id}>
                          <td className="px-3 py-2 font-bold text-gray-900">{line.name}</td>
                          <td className="px-3 py-2 text-right">{line.currentQuantity}</td>
                          <td className="px-3 py-2 text-right font-black text-blue-700">
                            {request.status === 'submitted' ? (
                              <input
                                type="number"
                                min="1"
                                value={requestQuantityDrafts[requestLineDraftKey(request.id, line.id)] ?? ''}
                                onChange={(event) => setRequestQuantityDrafts(prev => ({
                                  ...prev,
                                  [requestLineDraftKey(request.id, line.id)]: event.target.value,
                                }))}
                                className="min-h-10 w-28 rounded-lg border border-blue-200 px-2 text-right font-black text-blue-700 outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-100"
                                placeholder="待決定"
                              />
                            ) : (
                              formatRequestedQuantity(line.requestedQuantity)
                            )}
                          </td>
                          <td className="px-3 py-2">{line.unit}</td>
                          <td className="px-3 py-2 text-gray-600">
                            <div>{line.note || ''}</div>
                            {line.expiryBreakdown && (
                              <div className="mt-1 text-xs text-gray-500">{line.expiryBreakdown}</div>
                            )}
                            {line.quantityEditedBy && (
                              <div className="mt-1 text-xs text-gray-500">
                                {formatDateTime(line.quantityEditedAt)} 由 {line.quantityEditedBy} 修改
                              </div>
                            )}
                          </td>
                          <td className="px-3 py-2 text-right">
                            {request.status === 'submitted' ? (
                              <button
                                type="button"
                                onClick={() => handleUpdateRequestLineQuantity(request, line.id)}
                                disabled={isSavingId === `${request.id}:${line.id}`}
                                className="inline-flex min-h-10 items-center justify-center rounded-lg border border-blue-200 bg-white px-3 text-sm font-bold text-blue-700 hover:bg-blue-50 disabled:opacity-50"
                              >
                                儲存
                              </button>
                            ) : (
                              <span className="text-xs text-gray-400">已鎖定</span>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            ))}
          </div>
        </section>
      )}

      {activeTab === 'logs' && (
        <section className="rounded-2xl border border-gray-200 bg-white shadow-sm">
          <div className="flex items-center gap-3 border-b border-gray-200 p-4">
            <FileText className="text-gray-700" size={24} />
            <div>
              <h3 className="text-lg font-black text-gray-900">修改紀錄</h3>
              <p className="text-xs text-gray-500">最近 120 筆庫存與補貨異動</p>
            </div>
          </div>
          <div className="divide-y divide-gray-100">
            {visibleLogs.length === 0 ? (
              <div className="p-10 text-center text-gray-500">目前沒有修改紀錄</div>
            ) : visibleLogs.map(log => (
              <div key={log.id} className="grid gap-2 p-4 md:grid-cols-[150px_140px_1fr_180px] md:items-center">
                <div className="text-sm font-bold text-gray-900">{formatDateTime(log.createdAt)}</div>
                <div className="text-sm text-gray-600">{log.department}</div>
                <div>
                  <div className="font-bold text-gray-900">
                    {getActionLabel(log.action)}{log.itemName ? `：${log.itemName}` : ''}
                  </div>
                  <div className="mt-1 flex flex-wrap gap-2 text-xs text-gray-500">
                    {log.eventLabel && <span className="rounded-full bg-indigo-50 px-2 py-1 text-indigo-700">{log.eventLabel}</span>}
                    <span>{log.createdBy}</span>
                  </div>
                </div>
                {log.action === 'delete' ? (
                  <div className="flex items-center gap-2 text-sm font-bold text-red-600">
                    <AlertTriangle size={16} />
                    已移除
                  </div>
                ) : (
                  <div className="text-sm text-gray-500">{log.requestId ? `補貨單 ${log.requestId.slice(0, 8)}` : ''}</div>
                )}
              </div>
            ))}
          </div>
        </section>
      )}
    </div>
  );
};

export default InventoryManager;
